import { useCallback, useState } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { validateFileType, validateFileSize } from '../utils/fileHelpers';

interface FileUploaderProps {
  onUpload: (files: File[]) => void;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
}

export function FileUploader({
  onUpload,
  acceptedTypes = ['.pdf', '.docx', '.doc', '.png', '.jpg', '.jpeg'],
  maxSizeMB = 10,
  multiple = true,
  disabled = false,
  className = '',
}: FileUploaderProps) {
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      const newErrors: string[] = [];

      fileArray.forEach((file) => {
        if (!validateFileType(file)) {
          newErrors.push(`${file.name}: Tipo de archivo no válido`);
        } else if (!validateFileSize(file, maxSizeMB)) {
          newErrors.push(`${file.name}: El archivo excede ${maxSizeMB}MB`);
        } else {
          validFiles.push(file);
        }
      });

      setErrors(newErrors);

      if (validFiles.length > 0) {
        onUpload(validFiles);
      }
    },
    [maxSizeMB, onUpload]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      e.target.value = ''; // Reset input
    },
    [handleFiles]
  );

  return (
    <div className={`w-full ${className}`}>
      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : dragActive
            ? 'border-blue-500 bg-blue-50 scale-[1.01] shadow-lg'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        }`}
        onDragEnter={disabled ? undefined : handleDrag}
        onDragLeave={disabled ? undefined : handleDrag}
        onDragOver={disabled ? undefined : handleDrag}
        onDrop={disabled ? undefined : handleDrop}
      >
        <input
          type="file"
          id="file-upload-drop"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          onChange={handleChange}
          accept={acceptedTypes.join(',')}
          multiple={multiple}
          disabled={disabled}
          aria-label="Subir archivos"
        />

        <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4 ${
          disabled ? 'bg-gray-100' : dragActive ? 'bg-blue-100' : 'bg-gray-100'
        }`}>
          <Upload className={`h-8 w-8 ${
            disabled ? 'text-gray-300' : dragActive ? 'text-blue-500' : 'text-gray-400'
          }`} />
        </div>
        
        <p className={`text-base ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
          <span className={`font-semibold ${disabled ? 'text-gray-400' : 'text-blue-600'}`}>
            Haga clic para subir
          </span>{' '}
          o arrastre y suelte aquí
        </p>
        <p className="mt-2 text-sm text-gray-500">
          PDF, DOCX, PNG, JPG (máx. {maxSizeMB}MB por archivo)
        </p>
        {multiple && (
          <p className="mt-1 text-xs text-gray-400">
            Puede subir varios documentos a la vez - serán clasificados automáticamente
          </p>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mt-4 space-y-2">
          {errors.map((error, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg"
            >
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{error}</span>
              <button
                onClick={() => setErrors((prev) => prev.filter((_, i) => i !== index))}
                className="ml-auto p-1 hover:bg-red-100 rounded transition-colors"
                aria-label="Descartar error"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
